import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { legalDocument, legalDocumentPath } from "@/lib/legal/documents";

/**
 * The consent to the terms and the privacy policy, as ONE sentence with a box
 * in front of it — on both registration forms (W9 1-S). Lifted from the inline
 * box the forms carried until Kanzlei 7.3 (35fb0a339) replaced it with a
 * notice; back as a component so both forms render the same sentence, the
 * same links and the same error line.
 *
 * The links sit BESIDE the checkbox, not inside it. Two reasons, both
 * load-bearing on a consent surface: children of a `role="checkbox"` are
 * presentational, so a screen-reader user could not reach the Terms link at
 * all; and since the checkbox is keyboard-operable, a bubbling Space/Enter on
 * the focused link would open the document AND tick the box — "I read the
 * terms and it accepted them for me". Clicking the sentence still toggles;
 * clicking a link does not.
 *
 * Both links go through `/legal/:doc`, the one place that knows whether a
 * document is published (`lib/legal/documents.ts`).
 *
 * `isInvalid` means "the person tried to proceed without this tick". It sets
 * the visual state, `aria-invalid`, the `aria-describedby` pointer and the
 * error line together, so assistive tech and the eye get the same answer; an
 * untouched box carries none of them. The form owns that flag (set on a
 * failed click, cleared on the tick) and may hand in a `ref` to move focus to
 * the first box left unticked.
 */
export function TermsConsentCheckbox({
  isChecked,
  isInvalid,
  onChangeAction,
  testId,
  ref,
}: {
  isChecked: boolean;
  isInvalid: boolean;
  onChangeAction: (checked: boolean) => void;
  /** Distinct per surface so a test can tell the invite form from the signup form. */
  testId: string;
  ref?: React.Ref<HTMLDivElement>;
}) {
  const t = useTranslations("register");
  const tLegal = useTranslations("legal");
  const labelId = `${testId}-label`;
  const errorId = `${testId}-error`;
  const terms = legalDocument("terms");
  const privacy = legalDocument("privacy");

  return (
    <div>
      <div className="flex items-start">
        <CheckboxWithValidation
          ref={ref}
          isChecked={isChecked}
          isInvalid={isInvalid}
          testId={testId}
          alignStart
          ariaLabelledBy={labelId}
          ariaRequired
          ariaInvalid={isInvalid}
          ariaDescribedBy={isInvalid ? errorId : undefined}
          onCheckChangeAction={onChangeAction}
        />
        <span
          id={labelId}
          className="body-2-regular"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a")) return;
            onChangeAction(!isChecked);
          }}
        >
          {t("legal1")}{" "}
          <Link
            to={legalDocumentPath(terms.key)}
            target="_blank"
            rel="noopener noreferrer"
            className="body-2-bold underline"
          >
            {t("legal2")}
          </Link>{" "}
          {t("legal3")}{" "}
          <Link
            to={legalDocumentPath(privacy.key)}
            target="_blank"
            rel="noopener noreferrer"
            className="body-2-bold underline"
          >
            {t("legal4")}
          </Link>
        </span>
      </div>
      {isInvalid && (
        <p id={errorId} role="alert" className="mt-1 body-3-regular font-error">
          {tLegal("consentRequired")}
        </p>
      )}
    </div>
  );
}
